import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import navigationConfig from 'app/fuse-configs/navigationConfig';
import PropTypes from 'prop-types';

const ALL_ITEMS = navigationConfig.default ?? [];
const NAV_LINKS = ALL_ITEMS.filter(item => !item.children?.length);
const DROPDOWN_ITEMS = ALL_ITEMS.filter(item => item.children?.length > 0);

function NavLink({ to, children }) {
	const { pathname } = useLocation();
	const active = pathname === to || pathname.startsWith(to + '/');
	return (
		<Link
			to={to}
			style={{
				fontSize: '1.6rem',
				fontWeight: 500,
				color: active ? '#38BDF8' : 'rgba(255,255,255,0.85)',
				textDecoration: 'none',
				padding: '4px 0',
				borderBottom: active ? '2px solid #38BDF8' : '2px solid transparent',
				whiteSpace: 'nowrap',
				transition: 'color 0.15s',
			}}
			onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#fff'; }}
			onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
		>
			{children}
		</Link>
	);
}

NavLink.propTypes = {
  to: PropTypes.any,
  children: PropTypes.node,
};

function DropdownMenu({ item }) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	/*
	 * El panel se cierra escuchando mousedown fuera del contenedor y la tecla
	 * Escape. No se usa onBlur del botón: al pulsar un enlace el botón pierde el
	 * foco antes del mouseup, el panel se desmontaba y el clic nunca llegaba al
	 * enlace (login y registro quedaban inaccesibles, también por teclado).
	 */
	useEffect(() => {
		if (!open) {
			return undefined;
		}

		const handleMouseDown = event => {
			if (!containerRef.current?.contains(event.target)) {
				setOpen(false);
			}
		};
		const handleKeyDown = event => {
			if (event.key === 'Escape') {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open]);

	return (
		<div ref={containerRef} style={{ position: 'relative' }}>
			<button
				type="button"
				onClick={() => setOpen(o => !o)}
				aria-haspopup="true"
				aria-expanded={open}
				className="flex items-center gap-4 cursor-pointer"
				style={{
					background: '#1A56DB',
					color: '#fff',
					border: 'none',
					borderRadius: '8px',
					padding: '9px 20px',
					fontWeight: 700,
					fontSize: '1.6rem',
					boxShadow: '0 4px 12px rgba(26,86,219,0.35)',
					transition: 'background 0.15s',
					marginLeft: '8px',
				}}
				onMouseEnter={e => e.currentTarget.style.background = '#1648c0'}
				onMouseLeave={e => e.currentTarget.style.background = '#1A56DB'}
			>
				{item.title}
				<KeyboardArrowDownIcon style={{ fontSize: '18px', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
			</button>

			{open && (
				<div style={{
					position: 'absolute',
					top: 'calc(100% + 8px)',
					right: 0,
					background: '#fff',
					borderRadius: '10px',
					minWidth: '190px',
					boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
					border: '1px solid #E2E8F0',
					overflow: 'hidden',
				}}>
					{item.children.map(child => (
						<Link
							key={child.id}
							to={child.url}
							onClick={() => setOpen(false)}
							style={{ display: 'block', padding: '13px 20px', fontSize: '1.6rem', fontWeight: 500, color: '#1E3A8A', textDecoration: 'none' }}
							onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
							onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
						>
							{child.title}
						</Link>
					))}
				</div>
			)}
		</div>
	);
}

DropdownMenu.propTypes = {
  item: PropTypes.object,
};

export default function PublicNavbar() {
	const [mobileOpen, setMobileOpen] = useState(false);

	return (
		<>
			<nav
				className="flex items-center px-24 gap-32"
				style={{
					background: 'linear-gradient(90deg, #0F172A 0%, #1E3A8A 100%)',
					height: '68px',
					boxShadow: '0 2px 16px rgba(0,0,0,0.35)',
					position: 'sticky',
					top: 0,
					zIndex: 1100,
				}}
			>
				{/* Logo */}
				<Link to="/home" className="flex items-center shrink-0">
					<img src="assets/images/logos/logo.png" alt="EcoRep" style={{ height: '30px' }} />
				</Link>

				{/* ── Desktop: links + dropdowns agrupados a la derecha ── */}
				<div className="hidden lg:flex items-center gap-28" style={{ marginLeft: 'auto' }}>
					{NAV_LINKS.map(item => (
						<NavLink key={item.id} to={item.url}>{item.title}</NavLink>
					))}
					{DROPDOWN_ITEMS.map(item => (
						<DropdownMenu key={item.id} item={item} />
					))}
				</div>

				{/* ── Mobile: hamburger ── */}
				<button
					type="button"
					aria-label="Abrir menú"
					aria-expanded={mobileOpen}
					className="flex lg:hidden items-center cursor-pointer"
					style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#fff', padding: '6px', borderRadius: '8px' }}
					onClick={() => setMobileOpen(true)}
				>
					<MenuIcon style={{ fontSize: '30px' }} />
				</button>
			</nav>

			{/* ── Mobile drawer ── */}
			{mobileOpen && (
				<div
					className="fixed inset-0"
					style={{ zIndex: 1200, display: 'flex' }}
				>
					<button
						type="button"
						aria-label="Cerrar menú"
						className="absolute inset-0"
						style={{ background: 'rgba(0,0,0,0.5)', border: 'none', padding: 0, cursor: 'pointer' }}
						onClick={() => setMobileOpen(false)}
					/>

					<nav
						aria-label="Menú móvil"
						className="relative flex flex-col h-full overflow-hidden"
						style={{
							width: '280px',
							maxWidth: '85vw',
							background: 'linear-gradient(180deg, #0F172A 0%, #1E3A8A 100%)',
							boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
						}}
					>
						{/* Header */}
						<div
							className="flex items-center justify-between shrink-0"
							style={{ padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
						>
							<img src="assets/images/logos/logo.png" alt="EcoRep" style={{ height: '26px' }} />
							<button
								type="button"
								aria-label="Cerrar menú"
								onClick={() => setMobileOpen(false)}
								style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', padding: '4px', display: 'flex' }}
							>
								<CloseIcon style={{ fontSize: '24px' }} />
							</button>
						</div>

						{/* Links */}
						<div className="flex-1 overflow-y-auto" style={{ padding: '12px 0' }}>
							{NAV_LINKS.map(item => (
								<Link
									key={item.id}
									to={item.url}
									onClick={() => setMobileOpen(false)}
									style={{ display: 'block', padding: '14px 24px', fontSize: '1.6rem', fontWeight: 500, color: 'rgba(255,255,255,0.87)', textDecoration: 'none' }}
									onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#fff'; }}
									onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.87)'; }}
								>
									{item.title}
								</Link>
							))}

							{DROPDOWN_ITEMS.map(item => (
								<div key={item.id}>
									<div style={{ margin: '10px 24px', height: '1px', background: 'rgba(255,255,255,0.12)' }} />
									{item.children.map(child => (
										<Link
											key={child.id}
											to={child.url}
											onClick={() => setMobileOpen(false)}
											style={{ display: 'block', padding: '14px 24px', fontSize: '1.6rem', fontWeight: 600, color: '#38BDF8', textDecoration: 'none' }}
											onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
											onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
										>
											{child.title}
										</Link>
									))}
								</div>
							))}
						</div>
					</nav>
				</div>
			)}
		</>
	);
}
